import flash from "../../../services/flash";

export type HistoryItem<T> = {
  item: T;
  saved_at: string;
};
export function updateHistory<T>(
  action: (history: History<T>) => void,
  key: string,
  storage = window.localStorage,
) {
  let history: History<T>;
  const fromLocalStorage = storage.getItem(key);
  if (fromLocalStorage !== null) {
    history = Object.setPrototypeOf(
      JSON.parse(fromLocalStorage),
      History.prototype,
    );
  } else {
    history = new History(20);
  }
  try {
    action(history);
    storage.setItem(key, JSON.stringify(history));
  } catch (err) {
    const e: any = err;
    const isQuotaExceeded = Boolean(
      e &&
        (e.code === 22 ||
          e.name === "QuotaExceededError" ||
          e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
          (typeof e.message === "string" &&
            e.message.toLowerCase().includes("quota"))),
    );
    if (isQuotaExceeded) {
      if (
        confirm(
          "Your edit (undo) history is full. Clear site-map undo history so you can keep editing?",
        )
      ) {
        cleanUpUndoHistory(storage);
        flash.success("History cleared");
      }
      return;
    }
    throw err;
  }
}

// Implement a ring buffer so that our undo history does not grow continually causing us to overflow the local storage limits.
export class History<T> {
  maxLength = 10;
  buffer: Array<HistoryItem<T>>;
  isDirty = false;
  pointer = 0;
  storedRecords = 0;
  constructor(maxLength = 10) {
    this.buffer = Array<HistoryItem<T>>(maxLength);
    this.maxLength = maxLength;
  }
  push(item: T, comparator?: (a: T, b: T) => boolean): boolean {
    const current = this.current()?.item;
    const isSame =
      current != null &&
      (comparator
        ? comparator(current, item)
        : JSON.stringify(current) === JSON.stringify(item));
    if (isSame) {
      return false; // Don't store the same item twice in a row.
    }
    this.buffer[this.pointer] = { item, saved_at: new Date().toISOString() };
    this.pointer = (this.maxLength + this.pointer + 1) % this.maxLength;
    this.storedRecords = Math.min(this.storedRecords + 1, this.maxLength);
    this.isDirty = true;
    return true;
  }
  current(): HistoryItem<T> | null {
    if (this.storedRecords > 0) {
      return this.buffer[
        this.pointer === 0 ? this.maxLength - 1 : this.pointer - 1
      ];
    }
    return null;
  }
  undo(): HistoryItem<T> | null {
    if (this.storedRecords < 1) {
      return null;
    }
    const current = this.current();
    this.storedRecords--;
    this.pointer = this.pointer === 0 ? this.maxLength - 1 : this.pointer - 1;
    this.isDirty = true;
    return current;
  }
}

export function cleanUpUndoHistory(storage = window.localStorage) {
  const prefix = "site-map-history-";
  for (let i = storage.length - 1; i >= 0; i--) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) {
      storage.removeItem(k);
    }
  }
}
